/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {packageDuration, translate} from "utils/utility";
import {Package} from "types/entities/Package";
import {isWebpSupported} from "react-image-webp/dist/utils";
import PackageDetailContentsByPersonType
    from "pages/Front/Booking/components/PackageDetail/components/PackageDetailContentsByPersonType";
import PackageDetailPackageItems
    from "pages/Front/Booking/components/PackageDetail/components/PackageDetailPackageItems";
import {ServerImage} from "components/ServerImage";
import {PackageCategory} from "types/enums/PackageCategory";

interface Props {
    mainImageUrl: string;
}

const PackageDetailInfoImage = (props: Props) => {
    const mainImageUrl = isWebpSupported() ? props.mainImageUrl.replace("jpg", "jpg") : props.mainImageUrl;

    return (
        <div className={"PackageDetailInfoImage w-full sm:w-1/3 xl:w-2/5 flex flex-col flex-grow relative"}>
            <div className={"flex flex-row flex-grow"}>
                <ServerImage alt={"Package Detail"} className={"aspect-[3/2] xl:aspect-[4/3] w-full h-full object-cover"} src={mainImageUrl} />
            </div>
        </div>
    );
};

interface PackageDetailProps {
    packageDetail: Package;
}

const PackageDetailInfoTitle = (props: PackageDetailProps) => {
    const {t} = useTranslation();
    return (
        <div className={"PackageDetailInfoTitle flex flex-col sm:flex-row justify-center items-stretch border-b border-gray-600/50 self-stretch"}>
            <PackageDetailInfoImage mainImageUrl={props.packageDetail.main_image_url} />
            <main className={"PackageDetailInfoActivities px-4 sm:px-8 w-full sm:w-2/3 xl:w-7/12 py-4 xl:py-6 flex flex-col"}>
                <h3 className={"text-2xl font-bold mb-4 xl:mb-6 text-primary-800"}>{translate(props.packageDetail.name)}</h3>

                <div className={" text-sm text-gray-800 whitespace-pre-line"}>
                    {props.packageDetail.description_html && translate(props.packageDetail.description_html).length !== 0 ? (
                        <div dangerouslySetInnerHTML={{__html: translate(props.packageDetail.description_html)}}></div>
                    ) : (
                        <span className={"font-medium "}>{translate(props.packageDetail.description)}</span>
                    )}
                </div>
                {props.packageDetail.category !== PackageCategory.Event && <hr className={"my-4"} />}
                {props.packageDetail.category !== PackageCategory.Event && (
                    <div className={"PackageDetailContents mb-0 sm:mb-2 flex flex-col flex-grow justify-start items-start"}>
                        <h5 className={"mb-4 text-center font-medium"}>{t("What does this package contain?")}</h5>
                        <div className={"PackageDuration hidden flex-col items-start justify-start mb-4 leading-4"}>
                            <b>
                                ~ {packageDuration(props.packageDetail)} {t("minutes")} {t("of selected activities")}
                            </b>
                            <PackageDetailContentsByPersonType key={props.packageDetail.id} packageDetail={props.packageDetail} />
                        </div>
                        <div className={"flex flex-col justify-end w-full flex-grow"}>
                            <PackageDetailPackageItems packageDetail={props.packageDetail} />
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default PackageDetailInfoTitle;
